import React, { useState, useEffect, useRef } from "react";
import styles from "./SAKPreview.module.css";
import { CardFront } from "./CardFront";
import { CardBack } from "./CardBack";

interface SAKPreviewProps {
  imageUrl?: string;
  status?:
    | "photo-review"
    | "photo-rejected"
    | "questionnaire"
    | "post-order-flow-completed"
    | "default";
  surveyCompleted?: boolean;
}

const PREVIEW_HEIGHT = 200;
const CARD_PADDING_SIDES = 16;
const CARD_PADDING_BOTTOM = 32;
const TOTAL_HORIZONTAL_PADDING = CARD_PADDING_SIDES * 2;
const TOTAL_VERTICAL_PADDING = CARD_PADDING_SIDES + CARD_PADDING_BOTTOM;

// Portrait and landscape limits based on dimension constants
const MAX_LARGE_DIMENSION = 3;
const MAX_SMALL_DIMENSION = 2;
const MIN_ASPECT_RATIO = MAX_SMALL_DIMENSION / MAX_LARGE_DIMENSION;
const MAX_ASPECT_RATIO = MAX_LARGE_DIMENSION / MAX_SMALL_DIMENSION;

export const SAKPreview: React.FC<SAKPreviewProps> = ({
  imageUrl,
  status = "default",
  surveyCompleted = false
}) => {
  const [cardHeight, setCardHeight] = useState<string>("85%");
  const [showCardBack, setShowCardBack] = useState(false);
  const [cardDimensions, setCardDimensions] = useState({ width: 0, height: 0 });
  const cardFrontRef = useRef<HTMLDivElement>(null);
  const cardBackRef = useRef<HTMLDivElement>(null);
  const canShowCardBack = status !== "post-order-flow-completed" || surveyCompleted;
  const confettiRef = useRef<HTMLDivElement>(null);
  const whooshRef = useRef<HTMLDivElement>(null);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const calculateCardDimensions = (dimensions: { width: number; height: number }) => {
    const { width, height } = dimensions;
    let aspectRatio = width / height;

    // Constrain aspect ratio if needed
    if (aspectRatio > MAX_ASPECT_RATIO) {
      aspectRatio = MAX_ASPECT_RATIO;
    } else if (aspectRatio < MIN_ASPECT_RATIO) {
      aspectRatio = MIN_ASPECT_RATIO;
    }

    // Determine card height based on aspect ratio
    const heightPercentage = aspectRatio >= 1 ? 0.7 : 0.85;
    const cardHeight = PREVIEW_HEIGHT * heightPercentage;

    // Calculate content dimensions (excluding padding)
    const contentHeight = cardHeight - TOTAL_VERTICAL_PADDING;
    const contentWidth = contentHeight * aspectRatio;

    // Calculate final card width including padding
    const cardWidth = contentWidth + TOTAL_HORIZONTAL_PADDING;

    return {
      width: cardWidth,
      height: cardHeight
    };
  };

  const handleImageLoad = (dimensions: { width: number; height: number }) => {
    const { width, height } = dimensions;
    const aspectRatio = width / height;
    const newHeight = aspectRatio >= 1 ? "70%" : "85%";
    setCardHeight(newHeight);

    const newDimensions = calculateCardDimensions(dimensions);
    setCardDimensions(newDimensions);

    // Show the card back unless the user opted not to complete the survey
    if (canShowCardBack && newDimensions.width > 0 && newDimensions.height > 0) {
      setShowCardBack(true);
    }
  };

  useEffect(() => {
    if (!imageUrl) {
      setCardHeight("85%");

      // Calculate dimensions for placeholder (108x128)
      const placeholderDimensions = calculateCardDimensions({ width: 108, height: 128 });
      setCardDimensions(placeholderDimensions);
      if (canShowCardBack) {
        setShowCardBack(true);
      }
    } else {
      setShowCardBack(false);
    }
  }, [imageUrl, canShowCardBack]);

  // Update showCardBack when surveyCompleted changes
  useEffect(() => {
    if (canShowCardBack && cardDimensions.width > 0 && cardDimensions.height > 0) {
      setShowCardBack(true);
    } else {
      setShowCardBack(false);
    }
  }, [canShowCardBack, cardDimensions]);

  useEffect(() => {
    if (cardFrontRef.current && cardDimensions.width > 0 && cardDimensions.height > 0) {
      const frontCard = cardFrontRef.current;
      const backCard = cardBackRef.current;
      const confetti = confettiRef.current;
      const whoosh = whooshRef.current;
      const customBezier = "cubic-bezier(0.59, 0, 0.23, 0.98)";
      frontCard.style.transition = `transform 0.3s ${customBezier}, z-index 0s`;
      confetti.style.transition = `transform 0.5s ease-out`;
      whoosh.style.transition = `transform 0.3s ${customBezier}`;

      // Clear any existing animation timeout before animations
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }

      backCard.style.transition = `transform 0.3s ${customBezier}, z-index 0s`;

      if (status === "post-order-flow-completed") {
        // Initial position

        // Hide the back card and reposition the front card if survey not completed
        if (!showCardBack) {
          backCard.style.opacity = "0"; // Hide back card
          frontCard.style.transform = "translate(0, 0)";
          frontCard.style.zIndex = "2";
        } else {
          frontCard.style.transform = "translate(-30%, -8px)";
          backCard.style.transform = "translate(30%, 8px)";
          frontCard.style.zIndex = "1";
          backCard.style.zIndex = "2";
        }

        whoosh.style.opacity = "0"; // Hide whoosh to start

        // Animation sequence
        const sequence = async () => {
          await new Promise((resolve) => {
            confetti.style.transform = "scale(1)";
            confetti.style.opacity = "1";
            setTimeout(resolve, 1000);
          });

          // 1. Move back card 100% to the right and rotate
          await new Promise((resolve) => {
            backCard.style.transition = `transform 0.5s ${customBezier}`;
            backCard.style.transform = "translate(100%, 8px) rotate(15deg)";
            setTimeout(resolve, 500);
          });

          // 2. Change z-index and prepare for return
          backCard.style.zIndex = "1";
          frontCard.style.zIndex = "2";

          // 3. Return cards to center with rotation
          await new Promise((resolve) => {
            backCard.style.transition = `transform 0.5s ${customBezier}`;
            frontCard.style.transition = `transform 0.5s ${customBezier}`;
            backCard.style.transform = "translate(0, 0) rotate(0deg)";
            frontCard.style.transform = "translate(0, 0)";
            setTimeout(resolve, 800);
          });

          // 4. Hide back card and move front card and whoosh to the right
          await new Promise((resolve) => {
            backCard.style.opacity = "0"; // Hide back card
            frontCard.style.transition = `transform 0.7s ${customBezier}`;
            whoosh.style.transition = `transform 0.7s ${customBezier}`;
            frontCard.style.transform = "translate(100px, 0) rotate(5deg)";
            whoosh.style.opacity = "1";
            whoosh.style.transform = "translate(-5px, -50%)";
            setTimeout(resolve, 1200);
          });

          // 5. Back to center
          await new Promise((resolve) => {
            frontCard.style.transition = `transform 0.7s ${customBezier}`;
            whoosh.style.transition = `transform 0.7s ${customBezier}`;
            frontCard.style.transform = "translate(0, 0)";
            whoosh.style.transform = "translate(-65%, -50%)";
            setTimeout(resolve, 800);
          });

          // 6. Back to the right
          await new Promise((resolve) => {
            frontCard.style.transition = `transform 0.7s ${customBezier}`;
            whoosh.style.transition = `transform 0.7s ${customBezier}`;
            frontCard.style.transform = "translate(100px, 0) rotate(5deg)";
            whoosh.style.transform = "translate(-5px, -50%)";
            setTimeout(resolve, 1200);
          });

          // 7. Back to center
          await new Promise((resolve) => {
            frontCard.style.transition = `transform 0.7s ${customBezier}`;
            whoosh.style.transition = `transform 0.7s ${customBezier}`;
            frontCard.style.transform = "translate(0, 0)";
            whoosh.style.transform = "translate(-65%, -50%)";
            setTimeout(resolve, 800);
          });

          // 6. Back to the right
          await new Promise((resolve) => {
            frontCard.style.transition = `transform 0.7s ${customBezier}`;
            whoosh.style.transition = `transform 0.7s ${customBezier}`;
            frontCard.style.transform = "translate(100px, 0) rotate(5deg)";
            whoosh.style.transform = "translate(-5px, -50%)";
            setTimeout(resolve, 1200);
          });

          // 7. Back to center
          await new Promise((resolve) => {
            frontCard.style.transition = `transform 0.7s ${customBezier}`;
            whoosh.style.transition = `transform 0.7s ${customBezier}`;
            frontCard.style.transform = "translate(0, 0)";
            whoosh.style.transform = "translate(-65%, -50%)";
            setTimeout(resolve, 800);
          });
        };

        // Start the animation sequence
        sequence();
      } else if (
        status === "photo-review" ||
        status === "photo-rejected" ||
        status === "questionnaire"
      ) {
        frontCard.style.transform = "translate(-30%, -8px)";
        backCard.style.transform = "translate(30%, 8px)";

        if (status === "questionnaire") {
          frontCard.style.zIndex = "1";
          backCard.style.zIndex = "2";
        } else {
          frontCard.style.zIndex = "2";
          backCard.style.zIndex = "1";
        }
      } else {
        frontCard.style.transform = "translate(0, 0)";
        frontCard.style.zIndex = "2";
        backCard.style.transform = "translate(0, 0)";
        backCard.style.zIndex = "1";
      }
    }

    // Cleanup function
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [showCardBack, status, surveyCompleted, cardDimensions]);

  return (
    <div className={styles.preview}>
      <div className={styles.confetti} ref={confettiRef}></div>
      <div className={styles.whoosh} ref={whooshRef}></div>
      <CardFront
        ref={cardFrontRef}
        imageUrl={imageUrl}
        height={cardHeight}
        onImageLoad={handleImageLoad}
      />
      <CardBack
        visible={showCardBack}
        ref={cardBackRef}
        width={cardDimensions.width}
        height={cardDimensions.height}
        status={status}
      />
    </div>
  );
};
